import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Customer,
  Appointment,
  Service,
  QuestionnaireTemplate,
  Question,
  QuestionnaireSubmission,
  SubmissionEditEntry,
  MedicalCertificate,
  SystemConfig,
  Venue,
  AddOn,
  BookingAvailabilitySlot
} from '@/types';
import {
  Calendar,
  FileText,
  CheckCircle,
  Clock,
  LogOut,
  FileQuestion,
  ChevronRight,
  User as UserIcon,
  Loader2,
  MapPin,
  Edit3,
  ChevronLeft,
  MessageSquare,
  AlertTriangle,
  XCircle,
  Download
} from 'lucide-react';
import { normalizeErrorMessage } from '@/lib/errorUtils';
import { getAppointmentServiceIds, getAppointmentServiceNames } from '@/lib/appointmentServices';
import { ApiError } from '@/lib/apiError';
import { showAlertModal, showConfirmModal } from '@shared/components/common/AlertModal';
import { HealthAssessmentView } from '@shared/components/HealthAssessmentView';
import {
  filterPastAvailabilitySlots,
  getAvailableTimeSlotsForDate,
  isDateBlockedBySchedule
} from '@/lib/scheduling';
import { formatUkDate, normalizeDateOnly } from '@/lib/dates';
import { buildMapById } from '@/lib/collections';
import {
  classifyMagicLinkTarget,
  findPendingFormForAppointment
} from '../lib/autoOpenQuestionnaire';
import { api } from '@/services/api';
import { createId } from '@/lib/ids';
import {
  buildCertificateFileName,
  buildCertificateHtml,
  openCertificatePrintWindow,
  resolveCertificateClientName
} from '@/lib/certificateRenderer';
import { downloadCertificatePdf } from '@/lib/certificatePdf';

interface CustomerPortalProps {
  customer: Customer;
  appointments: Appointment[];
  services: Service[];
  venues: Venue[];
  addOns: AddOn[];
  questionnaires: QuestionnaireTemplate[];
  submissions: QuestionnaireSubmission[];
  certificates?: MedicalCertificate[];
  systemConfig?: SystemConfig;
  onLogout: () => void;
  onSubmitQuestionnaire: (submission: QuestionnaireSubmission) => Promise<void>;
  onEditSubmission: (submissionId: string, answers: Record<string, string>) => Promise<void>;
  onUpdateBooking: (appointment: Appointment) => Promise<void>;
  onForceRefresh?: () => Promise<void> | void;
  initialQuestionnaireAppId?: string | null;
  /** True once the initial data load (and any in-flight refresh) has finished. */
  isDataSettled?: boolean;
}

const RESCHEDULE_MIN_HOURS = 48;

const hoursUntilAppointment = (appointment: Appointment): number | null => {
  const date = normalizeDateOnly(appointment.date);
  const time = (appointment.time || '').trim();
  if (!date || !time) return null;
  const start = new Date(`${date}T${time.length === 5 ? `${time}:00` : time}`);
  const ms = start.getTime();
  if (!Number.isFinite(ms)) return null;
  return (ms - Date.now()) / (1000 * 60 * 60);
};

const isAppointmentWithin48Hours = (appointment: Appointment): boolean => {
  const hours = hoursUntilAppointment(appointment);
  return hours !== null && hours < RESCHEDULE_MIN_HOURS;
};

export const CustomerPortal: React.FC<CustomerPortalProps> = ({
  customer,
  appointments,
  services,
  venues,
  addOns,
  questionnaires,
  submissions,
  certificates = [],
  systemConfig,
  onLogout,
  onSubmitQuestionnaire,
  onEditSubmission,
  onUpdateBooking,
  onForceRefresh,
  initialQuestionnaireAppId = null,
  isDataSettled = false
}) => {
  const [activeTab, setActiveTab] = useState<'bookings' | 'history'>('bookings');

  // Portal data is loaded once at sign-in, so a tab left open while staff
  // complete the appointment shows a stale empty "Medical History &
  // Certificates" section — which reads as a missing certificate. Silently
  // refetch when the customer opens that section or returns to the page.
  // Throttled so tab-flipping doesn't stack redundant fetches.
  const lastAutoRefreshAtRef = useRef(0);
  const requestSilentRefresh = () => {
    if (!onForceRefresh) return;
    const now = Date.now();
    if (now - lastAutoRefreshAtRef.current < 10_000) return;
    lastAutoRefreshAtRef.current = now;
    void onForceRefresh();
  };

  useEffect(() => {
    if (activeTab === 'history') requestSilentRefresh();
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') requestSilentRefresh();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Questionnaire State
  const [answeringAppId, setAnsweringAppId] = useState<string | null>(null);
  const [answeringTemplateId, setAnsweringTemplateId] = useState<string | null>(null);
  const [editingSubmissionId, setEditingSubmissionId] = useState<string | null>(null);
  const [formAnswers, setFormAnswers] = useState<Record<string, string>>({});
  const [questionnaireStep, setQuestionnaireStep] = useState(0);
  const [questionnaireTouchedSteps, setQuestionnaireTouchedSteps] = useState<number[]>([]);
  const [viewingSubmission, setViewingSubmission] = useState<QuestionnaireSubmission | null>(null);

  // Tracks which magic-link appointment we've already handled (opened its form
  // or shown an informational message), so we don't reopen/re-alert after the
  // customer closes it or after a background refresh.
  const autoHandledAppIdRef = useRef<string | null>(null);

  // Rescheduling State
  const [amendingAppId, setAmendingAppId] = useState<string | null>(null);
  const [rescheduleData, setRescheduleData] = useState<{
    venueId: string;
    date: Date | null;
    time: string;
  }>({
    venueId: '',
    date: null,
    time: ''
  });
  const [visibleRescheduleMonth, setVisibleRescheduleMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [downloadingCertificateAppId, setDownloadingCertificateAppId] = useState<string | null>(
    null
  );
  const [rescheduleAvailabilitySlots, setRescheduleAvailabilitySlots] = useState<
    BookingAvailabilitySlot[]
  >([]);
  const [hasRescheduleAvailabilityData, setHasRescheduleAvailabilityData] = useState(false);
  const [isLoadingRescheduleAvailability, setIsLoadingRescheduleAvailability] = useState(false);
  const servicesById = useMemo(() => buildMapById(services), [services]);
  const addOnsById = useMemo(() => buildMapById(addOns), [addOns]);
  const venuesById = useMemo(() => buildMapById(venues), [venues]);
  const questionnairesById = useMemo(() => buildMapById(questionnaires), [questionnaires]);
  const certificatesByAppointmentId = useMemo(() => {
    const map = new Map<string, MedicalCertificate>();
    certificates.forEach((certificate) => {
      if (!map.has(certificate.appointmentId)) {
        map.set(certificate.appointmentId, certificate);
      }
    });
    return map;
  }, [certificates]);

  const myAppointments = appointments.filter((a) => a.customerId === customer.id);
  const upcomingApps = myAppointments
    .filter((a) => a.status === 'booked')
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  const historyApps = myAppointments
    .filter((a) => a.status !== 'booked')
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  const getRequiredTemplateIds = (app: Appointment) => {
    const ids: string[] = [];
    const seen = new Set<string>();
    getAppointmentServiceIds(app).forEach((serviceId) => {
      const serviceTemplateId = servicesById.get(serviceId)?.questionnaireConfig?.templateId;
      if (serviceTemplateId && !seen.has(serviceTemplateId)) {
        seen.add(serviceTemplateId);
        ids.push(serviceTemplateId);
      }
    });
    (app.addOnIds || []).forEach((id) => {
      const addon = addOnsById.get(id);
      const templateId = addon?.questionnaireConfig?.templateId;
      if (templateId && !seen.has(templateId)) {
        seen.add(templateId);
        ids.push(templateId);
      }
    });
    return ids;
  };

  const getSubmittedTemplateIds = (app: Appointment) => {
    const submitted = new Set<string>();
    const serviceTemplateId = servicesById.get(app.serviceId)?.questionnaireConfig?.templateId;
    submissions
      .filter((s) => s.appointmentId?.toLowerCase() === app.id?.toLowerCase())
      .forEach((submission) => {
        if (submission.templateId) {
          submitted.add(submission.templateId);
        } else if (serviceTemplateId) {
          submitted.add(serviceTemplateId);
        }
      });
    return submitted;
  };

  const getPendingTemplateIds = (app: Appointment) => {
    const requiredIds = getRequiredTemplateIds(app);
    if (requiredIds.length === 0) return [];
    const submittedIds = getSubmittedTemplateIds(app);
    return requiredIds.filter((id) => !submittedIds.has(id));
  };

  const pendingForms = upcomingApps.flatMap((app) => {
    const pendingIds = getPendingTemplateIds(app);
    return pendingIds.map((templateId) => ({ app, templateId }));
  });

  const nextPending = pendingForms[0];
  const nextPendingService = nextPending ? servicesById.get(nextPending.app.serviceId) : null;
  const nextPendingTemplate = nextPending ? questionnairesById.get(nextPending.templateId) : null;

  const isQuestionnaireStepTouched = (step: number) => questionnaireTouchedSteps.includes(step);

  const markQuestionnaireStepTouched = (step: number) => {
    setQuestionnaireTouchedSteps((prev) => (prev.includes(step) ? prev : [...prev, step]));
  };

  const normalizeText = (value?: string) => (value || '').trim();

  const isQuestionAnswered = (question: Question): boolean => {
    const answer = normalizeText(formAnswers[question.id]);
    if (question.required && !answer) return false;
    if (question.type !== 'yes_no') return true;

    const requiresDetails =
      question.requireDetailsOn &&
      question.requireDetailsOn !== 'none' &&
      answer === question.requireDetailsOn;
    if (!requiresDetails) return true;

    const detailsAnswer = normalizeText(formAnswers[`${question.id}_details`]);
    return Boolean(detailsAnswer);
  };

  const isQuestionnaireStepComplete = (question: Question) => isQuestionAnswered(question);

  const questionnaireQuestionError = (question: Question): string | null => {
    const answer = normalizeText(formAnswers[question.id]);
    if (question.required && !answer) {
      return 'This question is required.';
    }

    const requiresDetails =
      question.type === 'yes_no' &&
      question.requireDetailsOn &&
      question.requireDetailsOn !== 'none' &&
      answer === question.requireDetailsOn;
    if (requiresDetails && !normalizeText(formAnswers[`${question.id}_details`])) {
      return 'Please provide additional details.';
    }

    return null;
  };

  const isQuestionnaireComplete = (questions: Question[]) =>
    questions.every((question) => isQuestionnaireStepComplete(question));

  const buildCertificateRender = (app: Appointment) => {
    const cert = certificatesByAppointmentId.get(app.id);
    if (!cert || !systemConfig) return null;

    return {
      html: buildCertificateHtml({
        template: systemConfig.templates.certificateTemplate.body,
        systemConfig,
        appointment: app,
        certificate: cert,
        clientName: resolveCertificateClientName(app)
      }),
      fileName: buildCertificateFileName({
        appointment: app,
        certificate: cert
      })
    };
  };

  const handlePrintCertificate = (app: Appointment) => {
    const render = buildCertificateRender(app);
    if (!render) {
      showAlertModal('Certificate not ready or template unavailable.');
      return;
    }
    openCertificatePrintWindow(render.html, render.fileName);
  };

  const handleDownloadCertificate = async (app: Appointment) => {
    const render = buildCertificateRender(app);
    if (!render) {
      showAlertModal('Certificate not ready or template unavailable.');
      return;
    }
    setDownloadingCertificateAppId(app.id);
    try {
      const ok = await downloadCertificatePdf(render.html, render.fileName);
      if (!ok) {
        showAlertModal('Could not generate the certificate PDF. Please try again.');
      }
    } finally {
      setDownloadingCertificateAppId(null);
    }
  };

  const startQuestionnaire = (app: Appointment, templateId: string) => {
    const serviceTemplateId = servicesById.get(app.serviceId)?.questionnaireConfig?.templateId;
    const alreadyDone = submissions.some((s) => {
      if (s.appointmentId?.toLowerCase() !== app.id?.toLowerCase()) return false;
      if (s.templateId) return s.templateId === templateId;
      return Boolean(serviceTemplateId && serviceTemplateId === templateId);
    });
    if (alreadyDone) {
      showAlertModal('You have already submitted this questionnaire.');
      return;
    }
    const template = questionnairesById.get(templateId);
    if (!template) {
      showAlertModal('Questionnaire template not available. Please contact support.');
      return;
    }
    setAnsweringAppId(app.id);
    setAnsweringTemplateId(templateId);
    setEditingSubmissionId(null);
    setFormAnswers({});
    setQuestionnaireStep(0);
    setQuestionnaireTouchedSteps([]);
    setIsSubmitting(false);
  };

  const startEditSubmission = (submission: QuestionnaireSubmission) => {
    const app = myAppointments.find((a) => a.id === submission.appointmentId);
    const template = questionnairesById.get(submission.templateId);
    if (!app || !template) {
      showAlertModal('Unable to load this questionnaire for editing. Please contact support.');
      return;
    }
    if (['completed', 'cancelled', 'noshow'].includes(app.status)) {
      setViewingSubmission(submission);
      return;
    }
    setAnsweringAppId(app.id);
    setAnsweringTemplateId(submission.templateId);
    setEditingSubmissionId(submission.id);
    setFormAnswers({ ...submission.answers });
    setQuestionnaireStep(0);
    setQuestionnaireTouchedSteps([]);
    setIsSubmitting(false);
  };

  // Resolve the questionnaire magic link (`?mode=questionnaire&appointment=...`).
  // The portal often mounts before the appointment/submission data has loaded,
  // so this depends on `pendingForms`/`myAppointments` to re-run once the data
  // arrives. `autoHandledAppIdRef` guards against re-opening the form (or
  // re-showing a message) after the customer closes it or a background refresh.
  useEffect(() => {
    if (!initialQuestionnaireAppId) return;
    if (autoHandledAppIdRef.current === initialQuestionnaireAppId) return;

    const pending = findPendingFormForAppointment(pendingForms, initialQuestionnaireAppId);
    if (pending) {
      autoHandledAppIdRef.current = initialQuestionnaireAppId;
      startQuestionnaire(pending.app, pending.templateId);
      return;
    }

    // No pending form matched. Figure out why so we can tell the customer,
    // instead of leaving them on a blank screen thinking the link is broken.
    const status = classifyMagicLinkTarget({
      appointmentId: initialQuestionnaireAppId,
      appointments: myAppointments,
      getRequiredTemplateIds,
      getPendingTemplateIds,
      dataSettled: isDataSettled
    });

    // `not-found` means the appointment isn't in the customer's data yet (still
    // loading) — stay silent and let the effect retry when data arrives.
    if (status === 'completed') {
      autoHandledAppIdRef.current = initialQuestionnaireAppId;
      showAlertModal(
        'This questionnaire has already been completed — there is nothing further to fill in for this appointment.'
      );
    } else if (status === 'not-required') {
      autoHandledAppIdRef.current = initialQuestionnaireAppId;
      showAlertModal('This appointment does not require a questionnaire.');
    } else if (status === 'unavailable') {
      // The data load has settled and the linked appointment is not on this
      // account — the link was made for a booking under a different email.
      // Warn loudly: staying silent here previously left the customer filling
      // in a DIFFERENT appointment's questionnaire (K2Ref-461FAF1334 incident).
      autoHandledAppIdRef.current = initialQuestionnaireAppId;
      void showConfirmModal(
        `This questionnaire link is for booking ${initialQuestionnaireAppId}, which is not on this account (you are signed in as ${customer.email}). ` +
          'If that booking was made with a different email address, sign out, then open the link from the email again and sign in with the address the booking was made under. ' +
          'Please do not fill in a questionnaire for a different appointment instead.',
        {
          title: 'Questionnaire link is for a different account',
          confirmText: 'Sign out',
          cancelText: 'Stay signed in',
          isDestructive: false
        }
      ).then((signOut) => {
        if (signOut) onLogout();
      });
    }
  }, [initialQuestionnaireAppId, pendingForms, myAppointments, isDataSettled]); // eslint-disable-line react-hooks/exhaustive-deps

  const closeQuestionnaireForm = () => {
    setAnsweringAppId(null);
    setAnsweringTemplateId(null);
    setEditingSubmissionId(null);
    setQuestionnaireStep(0);
    setQuestionnaireTouchedSteps([]);
  };

  const submitQuestionnaire = async (app: Appointment, templateId: string) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      if (editingSubmissionId) {
        await onEditSubmission(editingSubmissionId, formAnswers);
      } else {
        const submission: QuestionnaireSubmission = {
          id: createId(),
          appointmentId: app.id,
          templateId: templateId,
          answers: formAnswers,
          submittedAt: new Date().toISOString()
        };
        await onSubmitQuestionnaire(submission);
      }
      closeQuestionnaireForm();
    } catch (err: unknown) {
      const message = normalizeErrorMessage(err, '').toLowerCase();
      if ((err instanceof ApiError && err.status === 409) || message.includes('23505')) {
        closeQuestionnaireForm();
        showAlertModal('Questionnaire already recorded.');
      } else if (err instanceof ApiError) {
        // The server responded with an error — show it instead of blaming the
        // connection, so validation/permission failures are actionable.
        showAlertModal(normalizeErrorMessage(err, 'Error saving form. Please try again.'));
      } else {
        showAlertModal('Error saving form. Please check your connection.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelBooking = async (app: Appointment) => {
    if (isAppointmentWithin48Hours(app)) {
      showAlertModal(
        'Appointments can only be cancelled more than 48 hours in advance. Please contact us for assistance.'
      );
      return;
    }
    const confirmed = await showConfirmModal(
      'This will cancel your appointment. You will need to book a new visit if you still want to attend. Continue?',
      {
        title: 'Cancel Appointment',
        confirmText: 'Cancel Appointment',
        cancelText: 'Keep It',
        isDestructive: true
      }
    );
    if (!confirmed) return;
    setIsSubmitting(true);
    try {
      await onUpdateBooking({ id: app.id, status: 'cancelled' } as Appointment);
      showAlertModal('Your appointment has been cancelled.');
    } catch (err: unknown) {
      showAlertModal(
        normalizeErrorMessage(
          err,
          'Failed to cancel appointment. Please try again or contact support.'
        )
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const startReschedule = (app: Appointment) => {
    if (isAppointmentWithin48Hours(app)) {
      showAlertModal(
        'Appointments can only be changed more than 48 hours in advance. Please contact us for assistance.'
      );
      return;
    }
    const appDate = new Date(app.date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const isPastAppointment = appDate.getTime() < today.getTime();
    setAmendingAppId(app.id);
    setRescheduleData({
      venueId: app.venueId,
      date: isPastAppointment ? null : appDate,
      time: isPastAppointment ? '' : app.time
    });
    const initialMonth = isPastAppointment ? today : appDate;
    setVisibleRescheduleMonth(new Date(initialMonth.getFullYear(), initialMonth.getMonth(), 1));
    setIsSubmitting(false);
  };

  const handleRescheduleSubmit = async () => {
    if (!amendingAppId || !rescheduleData.date || !rescheduleData.time) return;

    setIsSubmitting(true);
    const originalApp = upcomingApps.find((a) => a.id === amendingAppId);
    if (!originalApp) return;

    const updatedApp = {
      id: originalApp.id,
      venueId: rescheduleData.venueId,
      // Local calendar day, not UTC: .toISOString() rolls the day back one for
      // UK/negative-offset users, rescheduling to the day before the one selected.
      date: normalizeDateOnly(rescheduleData.date),
      time: rescheduleData.time
    } as Appointment;

    try {
      await onUpdateBooking(updatedApp);
      setAmendingAppId(null);
      showAlertModal('Your appointment has been successfully rescheduled.');
    } catch (err: unknown) {
      showAlertModal(
        normalizeErrorMessage(
          err,
          'Failed to update appointment. Please try again or contact support.'
        )
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (!amendingAppId || !rescheduleData.date || !rescheduleData.venueId) {
      setRescheduleAvailabilitySlots([]);
      setHasRescheduleAvailabilityData(false);
      setIsLoadingRescheduleAvailability(false);
      return;
    }

    const originalApp = appointments.find(
      (appointment) => appointment.id === amendingAppId && appointment.status === 'booked'
    );
    if (!originalApp?.serviceId) {
      setRescheduleAvailabilitySlots([]);
      setHasRescheduleAvailabilityData(false);
      setIsLoadingRescheduleAvailability(false);
      return;
    }

    let isCurrent = true;
    setIsLoadingRescheduleAvailability(true);
    setHasRescheduleAvailabilityData(false);

    api
      .getBookingAvailability({
        venueId: rescheduleData.venueId,
        date: normalizeDateOnly(rescheduleData.date),
        serviceId: originalApp.serviceId,
        addOnIds: originalApp.addOnIds || [],
        excludeAppointmentId: amendingAppId
      })
      .then((result) => {
        if (!isCurrent) return;
        const slots = filterPastAvailabilitySlots(rescheduleData.date, result?.slots || []);
        setRescheduleAvailabilitySlots(slots);
        setHasRescheduleAvailabilityData(true);
        setRescheduleData((prev) => {
          if (!prev.time) return prev;
          const stillAvailable = slots.some((slot) => slot.time === prev.time && slot.available);
          return stillAvailable ? prev : { ...prev, time: '' };
        });
      })
      .catch(() => {
        if (!isCurrent) return;
        setRescheduleAvailabilitySlots([]);
        setHasRescheduleAvailabilityData(false);
      })
      .finally(() => {
        if (isCurrent) {
          setIsLoadingRescheduleAvailability(false);
        }
      });

    return () => {
      isCurrent = false;
    };
  }, [amendingAppId, rescheduleData.date, rescheduleData.venueId, appointments]);

  // --- Rendering Rescheduling Modal Content ---
  if (amendingAppId) {
    const app = upcomingApps.find((a) => a.id === amendingAppId);
    const appServiceIds = app ? getAppointmentServiceIds(app) : [];
    const serviceNames = app ? getAppointmentServiceNames(app, servicesById) : '';
    const selectedVenue = venuesById.get(rescheduleData.venueId);
    const now = new Date();
    const today = new Date(now);
    today.setHours(0, 0, 0, 0);
    const minBookableDate = new Date(today);
    const monthStart = new Date(
      visibleRescheduleMonth.getFullYear(),
      visibleRescheduleMonth.getMonth(),
      1
    );
    const monthEnd = new Date(
      visibleRescheduleMonth.getFullYear(),
      visibleRescheduleMonth.getMonth() + 1,
      0
    );
    const dates = Array.from({ length: monthEnd.getDate() }, (_, i) => {
      return new Date(
        visibleRescheduleMonth.getFullYear(),
        visibleRescheduleMonth.getMonth(),
        i + 1
      );
    });
    const leadingEmptyDays = monthStart.getDay();
    const minMonthStart = new Date(minBookableDate.getFullYear(), minBookableDate.getMonth(), 1);
    const canGoPrevMonth = monthStart.getTime() > minMonthStart.getTime();
    const weekdayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const handleMonthChange = (offset: number) => {
      const nextMonth = new Date(
        visibleRescheduleMonth.getFullYear(),
        visibleRescheduleMonth.getMonth() + offset,
        1
      );
      if (offset < 0 && nextMonth.getTime() < minMonthStart.getTime()) return;
      setVisibleRescheduleMonth(nextMonth);
      setRescheduleData((prev) => {
        if (!prev.date) return prev;
        const isDateInVisibleMonth =
          prev.date.getFullYear() === nextMonth.getFullYear() &&
          prev.date.getMonth() === nextMonth.getMonth();
        return isDateInVisibleMonth ? prev : { ...prev, date: null, time: '' };
      });
    };

    const getMinutes = (timeStr: string) => {
      const [h, m] = timeStr.split(':').map(Number);
      return h * 60 + m;
    };

    const getTotalDuration = () => {
      let duration = appServiceIds.reduce(
        (sum, sid) => sum + (servicesById.get(sid)?.durationMinutes || 0),
        0
      );
      if (duration <= 0) duration = 30;
      app?.addOnIds.forEach((id) => {
        const addon = addOnsById.get(id);
        if (addon) duration += addon.durationMinutes || 0;
      });
      return duration;
    };

    const requiredDuration = getTotalDuration();
    const generatedSlots = rescheduleData.date
      ? getAvailableTimeSlotsForDate(rescheduleData.date, selectedVenue?.schedule, {
          requiredDurationMinutes: requiredDuration
        })
      : [];
    const availabilityByTime = new Map(
      rescheduleAvailabilitySlots.map((slot) => [slot.time, slot])
    );
    const availableSlots = rescheduleData.date
      ? hasRescheduleAvailabilityData
        ? rescheduleAvailabilitySlots.map((slot) => slot.time)
        : generatedSlots
      : [];
    const fallbackBusyIntervals = !hasRescheduleAvailabilityData
      ? appointments
          .filter(
            (booking) =>
              booking.venueId === rescheduleData.venueId &&
              new Date(booking.date).toDateString() === rescheduleData.date?.toDateString() &&
              booking.status !== 'cancelled' &&
              booking.id !== amendingAppId
          )
          .map((booking) => {
            const bookingServiceIds = getAppointmentServiceIds(booking);
            let appDuration = bookingServiceIds.reduce(
              (sum, sid) => sum + (servicesById.get(sid)?.durationMinutes || 0),
              0
            );
            if (appDuration <= 0) appDuration = 30;
            booking.addOnIds.forEach((aid) => {
              const addon = addOnsById.get(aid);
              if (addon) appDuration += addon.durationMinutes || 0;
            });
            const start = getMinutes(booking.time);
            return { start, end: start + appDuration };
          })
      : [];
    const isSlotConflict = (time: string) => {
      const availabilitySlot = availabilityByTime.get(time);
      const slotStart = getMinutes(time);
      const slotEnd = slotStart + requiredDuration;
      const fallbackConflict = fallbackBusyIntervals.some(
        (interval) => slotStart < interval.end && slotEnd > interval.start
      );
      return availabilitySlot ? !availabilitySlot.available : fallbackConflict;
    };
    const hasSelectableSlot = availableSlots.some((time) => !isSlotConflict(time));
    const showNoSlots =
      Boolean(rescheduleData.date) &&
      !isLoadingRescheduleAvailability &&
      availableSlots.length === 0;
    const showFullyBooked =
      Boolean(rescheduleData.date) &&
      !isLoadingRescheduleAvailability &&
      availableSlots.length > 0 &&
      !hasSelectableSlot;
    const isSelectedSlotAvailable = rescheduleData.time
      ? !isSlotConflict(rescheduleData.time)
      : false;

    return (
      <div className="w-full px-4 sm:px-8 lg:px-12 xl:px-16 py-8 animate-fade-in">
        <button
          onClick={() => setAmendingAppId(null)}
          disabled={isSubmitting}
          className="mb-4 text-slate-500 hover:text-slate-800 flex items-center gap-1 disabled:opacity-50 transition-colors text-sm"
        >
          ← Keep Original Appointment
        </button>
        <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
          <div className="bg-slate-900 p-8 text-white">
            <h2 className="text-2xl font-bold">Reschedule Visit</h2>
            <p className="text-slate-400 mt-2">
              Update your location, date or time for appointment <strong>{amendingAppId}</strong>.
            </p>
            <div className="mt-4 inline-flex items-center gap-2 bg-white/10 px-3 py-1 rounded text-xs font-bold text-teal-400">
              {serviceNames}
            </div>
          </div>

          <div className="p-8 space-y-8">
            {/* Location Selection */}
            <div>
              <label className="block font-bold text-slate-800 mb-3 text-lg flex items-center gap-2">
                <MapPin className="w-5 h-5 text-teal-600" /> 1. Select Location
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {venues.map((v) => (
                  <button
                    key={v.id}
                    onClick={() =>
                      setRescheduleData({ ...rescheduleData, venueId: v.id, time: '' })
                    }
                    className={`p-4 border-2 rounded-xl text-left transition-all ${rescheduleData.venueId === v.id ? 'bg-teal-50 border-teal-500 ring-2 ring-teal-200' : 'bg-white border-slate-100 hover:border-slate-200'}`}
                  >
                    <div
                      className={`font-bold ${rescheduleData.venueId === v.id ? 'text-teal-900' : 'text-slate-800'}`}
                    >
                      {v.name}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">{v.city}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Date Selection */}
            {rescheduleData.venueId && (
              <div className="animate-fade-in">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                  <label className="block font-bold text-slate-800 text-lg flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-teal-600" /> 2. Choose New Date
                  </label>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleMonthChange(-1)}
                      disabled={!canGoPrevMonth}
                      className="w-8 h-8 rounded-lg border border-slate-200 bg-white text-slate-700 hover:border-teal-400 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center"
                      aria-label="Previous month"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <div className="text-sm font-bold text-slate-800 min-w-[140px] text-center">
                      {visibleRescheduleMonth.toLocaleDateString('en-GB', {
                        month: 'long',
                        year: 'numeric'
                      })}
                    </div>
                    <button
                      onClick={() => handleMonthChange(1)}
                      className="w-8 h-8 rounded-lg border border-slate-200 bg-white text-slate-700 hover:border-teal-400 flex items-center justify-center"
                      aria-label="Next month"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-7 gap-2 mb-2">
                  {weekdayLabels.map((label) => (
                    <div
                      key={label}
                      className="text-[10px] font-bold text-slate-400 text-center uppercase"
                    >
                      {label}
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-7 gap-2">
                  {Array.from({ length: leadingEmptyDays }).map((_, idx) => (
                    <div key={`empty-${idx}`} />
                  ))}
                  {dates.map((date) => {
                    const isSelected = rescheduleData.date?.toDateString() === date.toDateString();
                    const { blocked, reason } = isDateBlockedBySchedule(
                      date,
                      selectedVenue?.schedule
                    );
                    const beforeBookingWindow = date.getTime() < minBookableDate.getTime();
                    const isToday = date.toDateString() === now.toDateString();
                    const hasSlotsToday =
                      isToday && selectedVenue
                        ? getAvailableTimeSlotsForDate(date, selectedVenue?.schedule, {
                            requiredDurationMinutes: requiredDuration
                          }).length > 0
                        : true;
                    const noSlotsLeftToday = !blocked && isToday && !hasSlotsToday;
                    const isUnavailable = blocked || beforeBookingWindow || noSlotsLeftToday;
                    return (
                      <button
                        key={date.toISOString()}
                        disabled={isUnavailable}
                        title={
                          beforeBookingWindow
                            ? 'Bookings cannot be in the past.'
                            : blocked
                              ? reason
                              : noSlotsLeftToday
                                ? 'No remaining slots today.'
                                : reason
                        }
                        onClick={() => setRescheduleData((prev) => ({ ...prev, date, time: '' }))}
                        className={`py-3 px-1 rounded-lg border text-center transition-all ${isSelected ? 'bg-teal-600 text-white border-teal-600' : 'bg-white hover:border-teal-400'} ${isUnavailable ? 'opacity-40 cursor-not-allowed bg-slate-50' : ''}`}
                      >
                        <div className="text-[10px] font-bold uppercase opacity-75">
                          {date.getDate()}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Time Selection */}
            {rescheduleData.date && rescheduleData.date.getTime() >= minBookableDate.getTime() && (
              <div className="animate-fade-in">
                <label className="block font-bold text-slate-800 mb-3 text-lg flex items-center gap-2">
                  <Clock className="w-5 h-5 text-teal-600" /> 3. Select Time Slot
                </label>
                {isLoadingRescheduleAvailability ? (
                  <p className="text-sm text-slate-500 italic">Loading availability...</p>
                ) : showNoSlots ? (
                  <p className="text-sm text-slate-500 italic">
                    No available times on this date. Please pick another.
                  </p>
                ) : (
                  <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                    {availableSlots.map((time) => {
                      const availabilitySlot = availabilityByTime.get(time);
                      const isConflict = isSlotConflict(time);

                      return (
                        <button
                          key={time}
                          disabled={isConflict}
                          onClick={() => setRescheduleData({ ...rescheduleData, time })}
                          className={`py-2 px-3 text-xs rounded-lg border transition-all font-medium ${rescheduleData.time === time ? 'bg-teal-600 text-white border-teal-600' : isConflict ? 'bg-red-50 text-red-300 border-red-100 cursor-not-allowed line-through' : 'bg-white text-slate-700 border-slate-200 hover:border-teal-400'}`}
                        >
                          {time}
                          {availabilitySlot && availabilitySlot.capacity > 1
                            ? ` (${availabilitySlot.remaining} left)`
                            : ''}
                        </button>
                      );
                    })}
                  </div>
                )}
                {showFullyBooked && (
                  <div className="mt-3 bg-white border border-slate-200 rounded-xl p-3 text-center">
                    <p className="text-xs text-slate-500">This day is fully booked.</p>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="p-8 border-t bg-slate-50 flex justify-end">
            <button
              onClick={handleRescheduleSubmit}
              disabled={
                isSubmitting ||
                !rescheduleData.date ||
                !rescheduleData.time ||
                !isSelectedSlotAvailable
              }
              className="bg-teal-600 text-white px-10 py-4 rounded-xl font-bold hover:bg-teal-700 flex items-center gap-3 disabled:opacity-50 transition-all active:scale-95 shadow-lg shadow-teal-600/20"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Updating Record...
                </>
              ) : (
                'Confirm New Schedule'
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // --- Rendering Read-Only Submission View ---
  if (viewingSubmission) {
    const app = myAppointments.find((a) => a.id === viewingSubmission.appointmentId);
    const template = questionnairesById.get(viewingSubmission.templateId);

    if (app && template) {
      return (
        <div className="max-w-2xl mx-auto px-4 py-8 animate-fade-in">
          <button
            onClick={() => setViewingSubmission(null)}
            className="mb-6 text-slate-500 hover:text-slate-800 flex items-center gap-1"
          >
            ← Back
          </button>
          <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-8">
            <div className="mb-6 border-b pb-4">
              <h2 className="text-2xl font-bold text-slate-900">{template.name}</h2>
              <p className="text-slate-500 mt-1">{template.description}</p>
              <div className="mt-4 bg-blue-50 text-blue-800 text-sm p-3 rounded-lg">
                Reference: <strong>{app.id}</strong> | Visit Date: {formatUkDate(app.date, '—')} at{' '}
                {app.time}
              </div>
            </div>
            <div className="space-y-6">
              {(template.questions || []).map((question) => {
                const answer = viewingSubmission.answers[question.id] ?? '—';
                return (
                  <div
                    key={question.id}
                    className="border border-slate-100 rounded-xl p-5 bg-slate-50/80"
                  >
                    {question.category && (
                      <p className="text-[10px] font-black uppercase tracking-widest text-teal-600 mb-1">
                        {question.category}
                      </p>
                    )}
                    <p className="font-black text-slate-800 text-sm uppercase tracking-wide mb-2">
                      {question.label}
                    </p>
                    <p className="text-slate-700">{answer}</p>
                    {question.requireDetailsOn !== 'none' &&
                      viewingSubmission.answers[`${question.id}_details`] && (
                        <p className="mt-1 text-slate-500 text-sm italic">
                          {viewingSubmission.answers[`${question.id}_details`]}
                        </p>
                      )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      );
    }
  }

  // --- Rendering Questionnaire Form Mode ---
  if (answeringAppId) {
    const app = myAppointments.find((a) => a.id === answeringAppId);
    const template = answeringTemplateId ? questionnairesById.get(answeringTemplateId) : undefined;

    if (app && template) {
      const questions = template.questions || [];
      const activeQuestionIndex = Math.max(
        0,
        Math.min(questionnaireStep, Math.max(0, questions.length - 1))
      );
      const activeQuestion = questions[activeQuestionIndex];
      const isLastQuestion = activeQuestionIndex === questions.length - 1;
      const canSubmit = isQuestionnaireComplete(questions) && !isSubmitting;
      const canContinue = isLastQuestion
        ? canSubmit
        : activeQuestion
          ? isQuestionnaireStepComplete(activeQuestion) && !isSubmitting
          : false;
      const currentQuestionError = activeQuestion
        ? isQuestionnaireStepTouched(activeQuestionIndex)
          ? questionnaireQuestionError(activeQuestion)
          : null
        : null;

      const closeQuestionnaire = closeQuestionnaireForm;

      const setQuestionAnswer = (questionId: string, value: string) =>
        setFormAnswers((prev) => ({ ...prev, [questionId]: value }));

      const handleNextQuestion = () => {
        if (activeQuestion) {
          markQuestionnaireStepTouched(activeQuestionIndex);

          if (!isQuestionnaireStepComplete(activeQuestion)) {
            return;
          }

          const isConsentNo =
            activeQuestion.type === 'yes_no' &&
            activeQuestion.consentWarning &&
            normalizeText(formAnswers[activeQuestion.id]) === 'no';

          if (isConsentNo) {
            showConfirmModal(
              'Are you sure you do not consent? We are unable to carry out medicals without your consent.',
              { confirmText: 'Yes, I do not consent', cancelText: 'Go back', isDestructive: true }
            ).then((confirmed) => {
              if (!confirmed) return;
              if (isLastQuestion) {
                if (canSubmit) submitQuestionnaire(app, template.id);
              } else {
                setQuestionnaireStep(activeQuestionIndex + 1);
              }
            });
            return;
          }
        }

        if (isLastQuestion) {
          if (canSubmit) {
            submitQuestionnaire(app, template.id);
          }
          return;
        }

        setQuestionnaireStep(activeQuestionIndex + 1);
      };

      return (
        <div className="max-w-2xl mx-auto px-4 py-8 animate-fade-in">
          <button
            onClick={closeQuestionnaire}
            disabled={isSubmitting}
            className="mb-6 text-slate-500 hover:text-slate-800 flex items-center gap-1 disabled:opacity-50"
          >
            ← Cancel
          </button>
          <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-8">
            <div className="mb-6 border-b pb-4">
              <div className="flex items-center gap-3 mb-1">
                <h2 className="text-2xl font-bold text-slate-900">{template.name}</h2>
                {editingSubmissionId && (
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest bg-amber-100 text-amber-700 border border-amber-200">
                    Editing
                  </span>
                )}
              </div>
              <p className="text-slate-500 mt-1">{template.description}</p>
              <div className="mt-4 bg-blue-50 text-blue-800 text-sm p-3 rounded-lg">
                Reference: <strong>{app.id}</strong> | Visit Date: {formatUkDate(app.date, '—')} at{' '}
                {app.time}
              </div>
            </div>

            {questions.length > 0 ? (
              <>
                <div className="border border-slate-100 rounded-xl p-6 bg-slate-50/80">
                  {activeQuestion.category && (
                    <p className="text-[10px] font-black uppercase tracking-widest text-teal-600 mb-2">
                      {activeQuestion.category}
                    </p>
                  )}
                  <label className="block font-black text-slate-800 mb-3 text-sm uppercase tracking-wide">
                    {activeQuestion.label}{' '}
                    {activeQuestion.required && <span className="text-red-500">*</span>}
                  </label>

                  {activeQuestion.type === 'text' && (
                    <input
                      className="w-full border-2 border-slate-200 rounded-lg p-3 outline-none focus:border-teal-500 bg-white transition-all"
                      value={formAnswers[activeQuestion.id] || ''}
                      onChange={(e) => setQuestionAnswer(activeQuestion.id, e.target.value)}
                      disabled={isSubmitting}
                    />
                  )}
                  {activeQuestion.type === 'date' && (
                    <input
                      type="date"
                      className="w-full border-2 border-slate-200 rounded-lg p-3 outline-none focus:border-teal-500 bg-white transition-all"
                      value={formAnswers[activeQuestion.id] || ''}
                      min="1900-01-01"
                      max={new Date().toISOString().slice(0, 10)}
                      onChange={(e) => setQuestionAnswer(activeQuestion.id, e.target.value)}
                      disabled={isSubmitting}
                    />
                  )}
                  {activeQuestion.type === 'textarea' && (
                    <textarea
                      className="w-full border-2 border-slate-200 rounded-lg p-3 outline-none focus:border-teal-500 bg-white transition-all"
                      rows={4}
                      value={formAnswers[activeQuestion.id] || ''}
                      onChange={(e) => setQuestionAnswer(activeQuestion.id, e.target.value)}
                      disabled={isSubmitting}
                    />
                  )}
                  {activeQuestion.type === 'select' && (
                    <select
                      className="w-full border-2 border-slate-200 rounded-lg p-3 bg-white outline-none focus:border-teal-500"
                      value={formAnswers[activeQuestion.id] || ''}
                      onChange={(e) => setQuestionAnswer(activeQuestion.id, e.target.value)}
                      disabled={isSubmitting}
                    >
                      <option value="">Select option...</option>
                      {activeQuestion.options?.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  )}
                  {activeQuestion.type === 'multi_select' && (
                    <div className="space-y-2">
                      {activeQuestion.options?.map((opt) => {
                        const selected = (formAnswers[activeQuestion.id] || '')
                          .split(',')
                          .map((s) => s.trim())
                          .filter(Boolean);
                        const checked = selected.includes(opt);
                        return (
                          <label
                            key={opt}
                            className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                              checked
                                ? 'border-teal-500 bg-teal-50'
                                : 'border-slate-200 bg-white hover:border-slate-300'
                            } ${isSubmitting ? 'opacity-60 pointer-events-none' : ''}`}
                          >
                            <input
                              type="checkbox"
                              className="w-4 h-4 text-teal-600 rounded accent-teal-600"
                              checked={checked}
                              disabled={isSubmitting}
                              onChange={() => {
                                const next = checked
                                  ? selected.filter((s) => s !== opt)
                                  : [...selected, opt];
                                setQuestionAnswer(activeQuestion.id, next.join(', '));
                              }}
                            />
                            <span className="text-sm font-medium text-slate-700">{opt}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}

                  {activeQuestion.type === 'yes_no' && (
                    <div className="space-y-4">
                      {activeQuestion.allowNa && (
                        <p className="text-xs text-slate-500 italic">
                          Please choose N/A if not applicable
                        </p>
                      )}
                      <div className="flex gap-4">
                        <button
                          type="button"
                          onClick={() => setQuestionAnswer(activeQuestion.id, 'yes')}
                          className={`flex-1 py-3 rounded-xl font-black border-2 transition-all shadow-sm ${
                            formAnswers[activeQuestion.id] === 'yes'
                              ? 'bg-teal-50 border-teal-500 text-teal-700'
                              : 'bg-white border-slate-100 text-slate-400 hover:border-slate-200'
                          }`}
                          disabled={isSubmitting}
                        >
                          YES
                        </button>
                        <button
                          type="button"
                          onClick={() => setQuestionAnswer(activeQuestion.id, 'no')}
                          className={`flex-1 py-3 rounded-xl font-black border-2 transition-all shadow-sm ${
                            formAnswers[activeQuestion.id] === 'no'
                              ? 'bg-slate-50 border-slate-500 text-slate-700'
                              : 'bg-white border-slate-100 text-slate-400 hover:border-slate-200'
                          }`}
                          disabled={isSubmitting}
                        >
                          NO
                        </button>
                        {activeQuestion.allowNa && (
                          <button
                            type="button"
                            onClick={() => setQuestionAnswer(activeQuestion.id, 'n/a')}
                            className={`flex-1 py-3 rounded-xl font-black border-2 transition-all shadow-sm ${
                              formAnswers[activeQuestion.id] === 'n/a'
                                ? 'bg-orange-50 border-orange-400 text-orange-700'
                                : 'bg-white border-slate-100 text-slate-400 hover:border-slate-200'
                            }`}
                            disabled={isSubmitting}
                          >
                            N/A
                          </button>
                        )}
                      </div>
                      {activeQuestion.type === 'yes_no' &&
                        activeQuestion.requireDetailsOn &&
                        activeQuestion.requireDetailsOn !== 'none' &&
                        formAnswers[activeQuestion.id] === activeQuestion.requireDetailsOn && (
                          <div className="animate-fade-in-up bg-blue-50 border border-blue-100 p-4 rounded-xl">
                            <label className="block text-[10px] font-black text-blue-600 uppercase tracking-widest mb-2 flex items-center gap-2">
                              <MessageSquare className="w-3 h-3" />{' '}
                              {activeQuestion.detailsLabel || 'Please provide further details'}
                            </label>
                            <textarea
                              className="w-full border-2 border-white rounded-lg p-3 text-sm focus:ring-4 focus:ring-blue-100 outline-none transition-all bg-white"
                              rows={3}
                              placeholder="Type here..."
                              value={formAnswers[`${activeQuestion.id}_details`] || ''}
                              onChange={(e) =>
                                setQuestionAnswer(`${activeQuestion.id}_details`, e.target.value)
                              }
                              disabled={isSubmitting}
                            />
                          </div>
                        )}
                    </div>
                  )}

                  {currentQuestionError && (
                    <p className="mt-3 text-sm font-medium text-red-600">{currentQuestionError}</p>
                  )}
                </div>

                <div className="mt-8 pt-6 border-t flex flex-wrap justify-between items-center gap-3">
                  <button
                    onClick={() => setQuestionnaireStep(Math.max(0, activeQuestionIndex - 1))}
                    disabled={isSubmitting || activeQuestionIndex === 0}
                    className="bg-white text-slate-700 border border-slate-200 px-6 py-3 rounded-xl font-black hover:bg-slate-100 transition-all disabled:opacity-50"
                  >
                    Previous
                  </button>

                  <button
                    onClick={handleNextQuestion}
                    disabled={isSubmitting || !canContinue}
                    className="bg-teal-600 text-white px-10 py-4 rounded-2xl font-black hover:bg-teal-700 flex items-center gap-2 disabled:opacity-50 transition-all active:scale-95 shadow-xl shadow-teal-600/20"
                  >
                    {isSubmitting ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : isLastQuestion ? (
                      'Finalise & Submit Form'
                    ) : (
                      'Next Question'
                    )}
                  </button>
                </div>
              </>
            ) : (
              <div className="bg-amber-50 text-amber-800 rounded-xl border border-amber-200 p-4 mb-6">
                This questionnaire has no questions. You can submit it now.
              </div>
            )}

            {questions.length === 0 && (
              <div className="mt-8 pt-6 border-t flex justify-end">
                <button
                  onClick={() => submitQuestionnaire(app, template.id)}
                  disabled={isSubmitting}
                  className="bg-teal-600 text-white px-10 py-4 rounded-2xl font-black hover:bg-teal-700 flex items-center gap-2 disabled:opacity-50 transition-all active:scale-95 shadow-xl shadow-teal-600/20"
                >
                  {isSubmitting ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    'Finalise & Submit Form'
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      );
    }

    if (app && !template) {
      return (
        <div className="max-w-2xl mx-auto px-4 py-8 animate-fade-in">
          <button
            onClick={() => {
              setAnsweringAppId(null);
              setAnsweringTemplateId(null);
              setQuestionnaireStep(0);
              setQuestionnaireTouchedSteps([]);
            }}
            className="mb-6 text-slate-500 hover:text-slate-800 flex items-center gap-1"
          >
            ← Back to Dashboard
          </button>
          <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-8 text-center">
            <AlertTriangle className="w-10 h-10 text-orange-400 mx-auto mb-4" />
            <p className="text-slate-700 font-semibold">
              Questionnaire template is unavailable for this appointment.
            </p>
            <p className="text-slate-500 text-sm mt-2">
              Please contact support and quote reference {app.id}.
            </p>
          </div>
        </div>
      );
    }
  }

  // --- Main Dashboard View ---
  return (
    <div className="w-full px-4 sm:px-8 lg:px-12 xl:px-16 py-8 animate-fade-in">
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 mb-8 flex flex-col md:flex-row justify-between items-center gap-4">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center text-blue-600">
            <UserIcon className="w-8 h-8" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-slate-900">Welcome, {customer.name}</h2>
            <p className="text-slate-500">{customer.email}</p>
          </div>
        </div>
        <button
          onClick={onLogout}
          className="text-slate-500 hover:text-red-600 flex items-center gap-2 font-medium px-4 py-2 hover:bg-red-50 rounded-lg transition-colors"
        >
          <LogOut className="w-4 h-4" /> Sign Out
        </button>
      </div>

      {pendingForms.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 mb-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div>
            <p className="text-[10px] font-black uppercase tracking-widest text-amber-700 mb-2">
              Outstanding Forms
            </p>
            <p className="text-slate-800 font-bold">
              You have {pendingForms.length} outstanding form
              {pendingForms.length === 1 ? '' : 's'} to complete.
            </p>
            {nextPending && (
              <p className="text-slate-600 text-sm mt-1">
                Next: {nextPendingTemplate?.name || 'Questionnaire'} for{' '}
                {nextPendingService?.name || 'appointment'} on{' '}
                {formatUkDate(nextPending.app.date, '—')} at {nextPending.app.time}
              </p>
            )}
          </div>
          <button
            onClick={() =>
              nextPending && startQuestionnaire(nextPending.app, nextPending.templateId)
            }
            className="bg-amber-600 text-white px-6 py-3 rounded-xl font-bold hover:bg-amber-700 transition-all shadow-sm"
          >
            Complete Now
          </button>
        </div>
      )}

      <div className="flex border-b mb-6 overflow-x-auto whitespace-nowrap scrollbar-hide">
        <button
          onClick={() => setActiveTab('bookings')}
          className={`px-6 py-3 font-medium border-b-2 transition-colors flex items-center gap-2 ${activeTab === 'bookings' ? 'border-teal-600 text-teal-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
        >
          <Calendar className="w-4 h-4" /> Upcoming Visits
        </button>
        <button
          onClick={() => setActiveTab('history')}
          className={`px-6 py-3 font-medium border-b-2 transition-colors flex items-center gap-2 ${activeTab === 'history' ? 'border-teal-600 text-teal-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
        >
          <FileText className="w-4 h-4" /> Medical History & Certificates
        </button>
      </div>

      <div className="space-y-4">
        {(activeTab === 'bookings' ? upcomingApps : historyApps).length === 0 ? (
          <div className="text-center py-12 bg-slate-50 rounded-xl border border-dashed border-slate-300">
            <p className="text-slate-500">No records found for this section.</p>
          </div>
        ) : (
          (activeTab === 'bookings' ? upcomingApps : historyApps).map((app) => {
            const cardServiceNames = getAppointmentServiceNames(app, servicesById);
            const venue = venuesById.get(app.venueId);
            const cert = certificatesByAppointmentId.get(app.id);
            const requiredTemplateIds = getRequiredTemplateIds(app);
            const submittedTemplateIds = getSubmittedTemplateIds(app);
            const pendingTemplateIds = requiredTemplateIds.filter(
              (id) => !submittedTemplateIds.has(id)
            );
            const requiredTemplates = requiredTemplateIds.map((id) => ({
              id,
              name: questionnairesById.get(id)?.name || 'Questionnaire',
              isSubmitted: submittedTemplateIds.has(id)
            }));
            const isSubmitted = requiredTemplateIds.length > 0 && pendingTemplateIds.length === 0;
            const needsForm = pendingTemplateIds.length > 0;

            // Map templateId → submission so we can pass to edit / show history
            const submissionByTemplateId = new Map(
              submissions
                .filter((s) => s.appointmentId?.toLowerCase() === app.id?.toLowerCase())
                .map((s) => [s.templateId, s])
            );

            // Expiry check for history tab
            const isExpired = cert && new Date(cert.validUntil) < new Date();

            return (
              <div
                key={app.id}
                className={`bg-white rounded-xl border p-6 flex flex-col md:flex-row justify-between items-start md:items-center gap-6 shadow-sm hover:shadow-md transition-shadow ${isExpired ? 'border-orange-200' : 'border-slate-200'}`}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span
                      className={`px-2 py-1 rounded text-[10px] font-bold uppercase ${app.status === 'booked' ? 'bg-blue-100 text-blue-700' : app.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}`}
                    >
                      {app.status}
                    </span>
                    {cert && (
                      <span
                        className={`px-2 py-1 rounded text-[10px] font-black uppercase tracking-widest ${isExpired ? 'bg-orange-100 text-orange-700 border border-orange-200' : 'bg-green-50 text-green-700 border border-green-200'}`}
                      >
                        {isExpired ? 'Expired' : 'Valid Certificate'}
                      </span>
                    )}
                    <span className="text-slate-400 text-xs font-mono">Ref: {app.id}</span>
                  </div>
                  <h3 className="text-lg font-bold text-slate-900">{cardServiceNames}</h3>
                  <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-slate-500 mt-2 text-sm">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4 text-slate-300" /> {formatUkDate(app.date, '—')}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4 text-slate-300" /> {app.time}
                    </span>
                    <span className="flex items-center gap-1">
                      <MapPin className="w-4 h-4 text-slate-300" /> {venue?.name || 'Loading...'}
                    </span>
                  </div>
                  {requiredTemplates.length > 0 && (
                    <div className="mt-4">
                      <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">
                        Required Forms
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {requiredTemplates.map((template) => (
                          <button
                            key={template.id}
                            type="button"
                            onClick={() =>
                              !template.isSubmitted &&
                              activeTab === 'bookings' &&
                              startQuestionnaire(app, template.id)
                            }
                            disabled={template.isSubmitted || activeTab !== 'bookings'}
                            className={`px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-widest border flex items-center gap-1 ${template.isSubmitted ? 'bg-green-50 text-green-700 border-green-100' : 'bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-100'} ${template.isSubmitted || activeTab !== 'bookings' ? 'cursor-default' : ''}`}
                          >
                            {template.isSubmitted ? (
                              <CheckCircle className="w-3 h-3" />
                            ) : (
                              <FileQuestion className="w-3 h-3" />
                            )}
                            {template.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {requiredTemplateIds.map((tid) => {
                    const sub = submissionByTemplateId.get(tid);
                    if (!sub?.editLog?.length) return null;
                    return (
                      <div
                        key={tid}
                        className="mt-4 border border-slate-100 rounded-xl p-3 bg-slate-50 space-y-2"
                      >
                        <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 flex items-center gap-1">
                          <Clock className="w-3 h-3" /> Edit History —{' '}
                          {questionnairesById.get(tid)?.name || 'Form'}
                        </p>
                        {[...sub.editLog].reverse().map((entry: SubmissionEditEntry, i: number) => (
                          <div
                            key={i}
                            className="text-xs text-slate-600 bg-white rounded-lg border border-slate-100 px-3 py-2"
                          >
                            <p className="font-bold text-slate-500 mb-1">
                              {new Date(entry.editedAt).toLocaleString('en-GB', {
                                dateStyle: 'medium',
                                timeStyle: 'short'
                              })}
                            </p>
                            {Object.entries(entry.changes)
                              .filter(([key]) => !key.endsWith('_details'))
                              .map(([key, diff]) => {
                                const question = questionnairesById
                                  .get(tid)
                                  ?.questions?.find((q) => q.id === key);
                                const label = question?.label || key;
                                const detailKey = `${key}_details`;
                                const detailDiff = entry.changes[detailKey];
                                return (
                                  <div key={key} className="mb-1">
                                    <span className="font-semibold text-slate-700">{label}: </span>
                                    <span className="line-through text-red-400">
                                      {diff.from || '—'}
                                    </span>
                                    {' → '}
                                    <span className="text-green-600 font-medium">
                                      {diff.to || '—'}
                                    </span>
                                    {detailDiff && (
                                      <div className="ml-3 text-slate-400 italic">
                                        Details:{' '}
                                        <span className="line-through text-red-300">
                                          {detailDiff.from || '—'}
                                        </span>
                                        {' → '}
                                        <span className="text-green-500">
                                          {detailDiff.to || '—'}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                          </div>
                        ))}
                      </div>
                    );
                  })}
                  {cert && isExpired && (
                    <div className="mt-3 flex items-center gap-2 text-orange-600 text-xs font-bold bg-orange-50 w-fit px-3 py-1 rounded-lg">
                      <AlertTriangle className="w-3 h-3" /> Expiry passed on{' '}
                      {formatUkDate(cert.validUntil, '—')}
                    </div>
                  )}
                </div>

                <div className="flex flex-col gap-2 w-full md:w-[220px] md:shrink-0">
                  {activeTab === 'bookings' && (
                    <>
                      {needsForm ? (
                        <button
                          onClick={() =>
                            pendingTemplateIds[0] && startQuestionnaire(app, pendingTemplateIds[0])
                          }
                          className="bg-orange-50 text-orange-700 border border-orange-200 px-4 py-3 rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-orange-100 transition-all animate-pulse shadow-sm"
                        >
                          <FileQuestion className="w-4 h-4" /> Complete Form
                          {pendingTemplateIds.length > 1 ? 's' : ''}
                        </button>
                      ) : isSubmitted ? (
                        <>
                          <div className="text-green-600 flex items-center justify-center gap-2 bg-green-50 py-3 rounded-xl border border-green-100 text-sm font-bold shadow-sm px-4">
                            <CheckCircle className="w-4 h-4" /> Forms Complete
                          </div>
                          {!cert &&
                            requiredTemplateIds.map((tid) => {
                              const sub = submissionByTemplateId.get(tid);
                              if (!sub) return null;
                              return (
                                <button
                                  key={tid}
                                  onClick={() => startEditSubmission(sub)}
                                  disabled={isSubmitting}
                                  className="bg-white text-amber-600 border border-amber-200 px-4 py-2.5 rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-amber-50 transition-all shadow-sm disabled:opacity-50"
                                >
                                  <Edit3 className="w-4 h-4" />
                                  {['completed', 'cancelled', 'noshow'].includes(app.status)
                                    ? 'View Answers'
                                    : 'Edit Answers'}
                                </button>
                              );
                            })}
                        </>
                      ) : null}

                      {(() => {
                        const within48 = isAppointmentWithin48Hours(app);
                        const disabledTitle = within48
                          ? 'Appointments within 48 hours cannot be changed online. Please contact us for assistance.'
                          : undefined;
                        return (
                          <>
                            <button
                              onClick={() => startReschedule(app)}
                              disabled={within48 || isSubmitting}
                              title={disabledTitle}
                              className="bg-white text-teal-600 border border-teal-200 px-4 py-2.5 rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-teal-50 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white"
                            >
                              <Edit3 className="w-4 h-4" /> Reschedule Visit
                            </button>
                            <button
                              onClick={() => handleCancelBooking(app)}
                              disabled={within48 || isSubmitting}
                              title={disabledTitle}
                              className="bg-white text-red-600 border border-red-200 px-4 py-2.5 rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-red-50 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white"
                            >
                              <XCircle className="w-4 h-4" /> Cancel Visit
                            </button>
                            {within48 && (
                              <p className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 font-bold uppercase tracking-wide leading-snug flex items-start gap-1">
                                <AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" />
                                Within 48 hours — contact us to change or cancel.
                              </p>
                            )}
                          </>
                        );
                      })()}
                    </>
                  )}

                  {activeTab === 'history' && app.status === 'completed' && cert && (
                    <>
                      <button
                        onClick={() => handlePrintCertificate(app)}
                        className="bg-slate-900 text-white px-4 py-3 rounded-xl font-bold hover:bg-slate-800 flex items-center justify-center gap-2 shadow-sm transition-all"
                      >
                        <FileText className="w-4 h-4" /> View Certificate
                      </button>
                      <button
                        onClick={() => handleDownloadCertificate(app)}
                        disabled={downloadingCertificateAppId !== null}
                        className="bg-white text-slate-900 border border-slate-300 px-4 py-3 rounded-xl font-bold hover:bg-slate-50 flex items-center justify-center gap-2 shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {downloadingCertificateAppId === app.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Download className="w-4 h-4" />
                        )}{' '}
                        Download PDF
                      </button>
                    </>
                  )}
                  {app.healthAssessment && (
                    <div className="mt-4">
                      <HealthAssessmentView data={app.healthAssessment} />
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};
